import { Dispatch } from "redux";
import { ActionFuncResponse, CountryID } from "@al/base-lib";
import { ReduxStateObj } from "@al/base-lib/dist/ReduxHelper/types";

import { ProductDetailType, AddressItemType, SkuInfoType } from "../types";
import { needInstallmentConfig } from "../constants";

import redux from "./helper";

export async function getSkuPrice({
  dispatch,
  spuId,
  selectSku,
  detail,
  isFirst = false,
  count = 1,
  defaultAddress,
}: {
  dispatch: Dispatch<ReduxStateObj>;
  spuId: string;
  selectSku?: SkuInfoType;
  detail: ProductDetailType;
  // 是否第一次请求，如果是第一次请求，则不需要实时库存和自动选择sku
  isFirst?: boolean;
  // 购买数量
  count?: number;
  defaultAddress: AddressItemType;
}): Promise<ActionFuncResponse<any>> {
  // 这里的分期计划一期按照固定写死逻辑.
  const needInstallment = needInstallmentConfig[defaultAddress.countryId as CountryID];
  const params: any = {
    spuId: spuId,
    /**
     * 是否需要返回全部sku的库存信息（切换sku时不建议请求）
     */
    needAllSkuStock: isFirst,
    /**
     * 指定时，后端会返回最优sku（切换sku时不建议请求）
     */
    needBestSku: isFirst,
    /**
     * 是否需要分期计划
     */
    // FIXME 这里的分期计划一期按照固定写死逻辑.
    needInstallment: needInstallment,
    addressReqVo: {
      countryId: defaultAddress.countryId,
      provinceId: defaultAddress.provinceId,
      cityId: defaultAddress.cityId,
      districtId: defaultAddress.districtId,
      villageId: defaultAddress.villageId,
    },
    shopId: detail.shopId,
    vendorId: detail.vendorId,
    fulfillmentType: detail.fulfillmentType,
    spuName: detail.name,
    /**
     * 商品所属类别的ID集合
     */
    categoryIdList: detail.categoryInfos?.map((item) => item.id) || [],
  };

  if (selectSku) {
    params.skuVo = {
      /**
       * skuId
       */
      skuId: selectSku.skuId,
      /**
       * 购买数量
       */
      quantity: count,
      /**
       * sku原价-税后
       */
      originPrice: selectSku.originalPrice,
      /**
       * sku原价-税前
       */
      originPriceNoVat: selectSku.originalPriceNoVat,
      /**
       * 货品id，用于计算毛利率
       */
      goodsIds: selectSku.goodsIds,
      /**
       * 欧洲站税率（0-1之间），印尼为null
       */
      vat: detail.vat,
    };
  } else {
    params.skuVo = {
      skuId: 0,
      quantity: 1,
      originPrice: 0,
      goodsIds: [],
    };
  }
  return dispatch(redux.actions.asyncGetProductSkuInfos(params));
}

export async function getFreight({
  dispatch,
  currentSku,
  detail,
  count = 1,
  defaultAddress,
}: {
  dispatch: Dispatch<ReduxStateObj>;
  currentSku: SkuInfoType;
  detail: ProductDetailType;
  // 购买数量
  count?: number;
  defaultAddress: AddressItemType;
}): Promise<ActionFuncResponse<any>> {
  const params = {
    freightReqVo: {
      /**
       * spu Id
       */
      spuId: detail?.spuId,
      /**
       * 售卖类型（1：自营(采销)、2：POP(寄售)、3：API_JD商品(代售)、4：API_BL商品(代售)）
       */
      sellMode: detail?.sellMode,
      /**
       * sku Id
       */
      skuId: currentSku.skuId,
      /**
       * api平台的skuId，用于调用外部运费计算
       */
      apiSkuId: currentSku.apiSkuId,
      /**
       * 数量
       */
      qty: count,
      /**
       * 运费模板id
       */
      freightTemplateId: detail?.freightTemplateId,
      /**
       * 店铺ID
       */
      shopId: detail?.shopId,
      /**
       * 商户ID
       */
      vendorId: detail?.vendorId,
      /**
       * 长
       */
      length: currentSku.freightInfo?.length,
      /**
       * 宽
       */
      width: currentSku.freightInfo?.width,
      /**
       * 高
       */
      height: currentSku.freightInfo?.height,
      /**
       * 重
       */
      weight: currentSku.freightInfo?.weight,
    },
    addressReqVo: {
      /**
       * 国家id
       */
      countryId: defaultAddress?.countryId,
      /**
       * 省份id
       */
      provinceId: defaultAddress?.provinceId,
      /**
       * 城市id(渠道id)
       */
      cityId: defaultAddress?.cityId,
      /**
       * 区id(查库存时需要传)
       */
      districtId: defaultAddress?.districtId,
      /**
       * 乡id
       */
      villageId: defaultAddress?.villageId,
    },
  };
  return dispatch(redux.actions.asyncGetFreight(params));
}

export async function getInstallmentPlants({
  dispatch,
  currentSku,
  detail,
  count = 1,
  sellingPrice,
}: {
  dispatch: Dispatch<ReduxStateObj>;
  currentSku: SkuInfoType;
  detail: ProductDetailType;
  // 购买数量
  count?: number;
  sellingPrice?: number;
}): Promise<ActionFuncResponse<any>> {
  const params = {
    /**
     * spu Id
     */
    spuId: detail?.spuId,
    /**
     * sku Id
     */
    skuId: currentSku.skuId,
    /**
     * 数量
     */
    quantity: count,
    /**
     * 店铺ID
     */
    shopId: detail?.shopId,
    /**
     * 商户ID
     */
    vendorId: detail?.vendorId,
    /**
     * 商品所属类别的ID集合
     */
    categoryIdList: detail.categoryInfos?.map((item) => item.id) || [],
    spuName: detail.name,
    /*
     * 原价
     */
    originalPrice: currentSku.originalPrice,
    /*
     * 售价(为null时用原价代替)
     */
    sellingPrice: sellingPrice || currentSku.originalPrice,
    /*
     * 券后价
     */
    couponPrice: currentSku.couponPrice,
  };
  return dispatch(redux.actions.asyncGetInstallmentPlants(params));
}
